import PageHead from "../components/PageHead";
import CTA from "../components/CTA";

export default function About() {
  return (
    <>
      <PageHead
        crumb="About"
        eyebrow="About"
        title="The team behind Docket."
        lede="Docket is built by Devnco, a software consultancy that spent years building digital products for other people, kept running into the same problem, and decided to solve it properly."
      />

      <section className="section">
        <div className="container">
          <div className="split2">
            <div className="lead-block reveal">
              <div className="eyebrow" style={{ marginBottom: 22 }}>Our story</div>
              <h2>A consultancy that decided to build a product.</h2>
              <p className="mark">Docket is the problem we saw most often, finally built the way it should be.</p>
            </div>
            <div className="prose-col reveal d1">
              <p>Devnco Technologies is a software consultancy based in Pune, India. We design and build digital products for clients across India and the United States, working across industries, team sizes and technology stacks.</p>
              <p>
                That kind of work gives you a wide view. And from that view, one problem kept reappearing. Whatever the client and whatever the sector, a surprising amount of skilled human time was being spent reading documents and typing their contents into other systems. <strong>It was slow, it was expensive, and it was invisible</strong>, rarely on a roadmap, never owned by anyone.
              </p>
              <p>
                We solved it, project by project, more times than we can count. Eventually the pattern was obvious enough that building it once, properly, as a product, made more sense than rebuilding it again. <strong>That product is Docket.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-2 bordered">
        <div className="container">
          <div className="s-head reveal" style={{ marginBottom: 46 }}>
            <div className="eyebrow">How we build</div>
            <h2>Three convictions, built into the product.</h2>
            <p className="lede">Years on the client side of software shaped a clear point of view. It is visible in every part of how Docket works.</p>
          </div>
          <div className="principles reveal">
            {[
              ["01", "Trained, not generic", "A document tool should learn your documents, not force them to fit a vendor's template. Docket extracts what it has been taught, and when it meets a document it does not know, it says so rather than guessing."],
              ["02", "Built for the operator", "The person who processes documents every day should be able to run the platform, train models, review results, read the reports, without a consultant or a data scientist standing beside them."],
              ["03", "Transparent by default", "Confidence scores on every field, an audit trail behind every action, summaries you can read. Automation earns trust only when you can see exactly what it did, and why."],
            ].map(([n, h, p]) => (
              <div key={n} className="principle">
                <div className="principle__no">{n}</div>
                <h3>{h}</h3>
                <p>{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="split2">
            <div className="lead-block reveal">
              <div className="eyebrow" style={{ marginBottom: 22 }}>Why Docket</div>
              <h2>The boring problem worth solving well.</h2>
              <p className="mark">Get the document layer right, and a lot of other things quietly get easier.</p>
            </div>
            <div className="prose-col reveal d1">
              <p>Document processing is not a glamorous problem. It does not get conference keynotes. But it sits underneath finance, operations, legal and compliance in almost every organisation, and when it is slow or error-prone, everything built on top of it inherits the cost.</p>
              <p>We built Docket because we believe that layer deserves a real platform: <strong>one that learns your documents, keeps a person in the loop where judgement matters, and proves what it did.</strong> Not a generic model that guesses, and not a rigid tool that takes months to bend into shape.</p>
              <p>We are still a consultancy at heart, which means we would rather show you than tell you. The best way to understand Docket is to watch it work on your documents, so that is exactly what we offer.</p>
            </div>
          </div>
        </div>
      </section>

      <CTA
        eyebrow="Work with us"
        title="Let's put Docket to work on <em>your</em> documents."
        text="Tell us about the document types slowing your team down. We will set up a pilot, and you will see the result on your own data, not a demo set."
        secondary={{ to: "mailto:hello@devnco.in", label: "Email the team" }}
        emailFallback
      />
    </>
  );
}
